import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Weight, Ruler, Truck as TruckIcon, Layers, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { trailers as fallbackTrailers, categories } from '../mock';
import { getTrailers } from '../lib/api';

export default function TrailerSection({ onBook }) {
  const [active, setActive] = useState('All');
  const [trailers, setTrailers] = useState(fallbackTrailers);

  useEffect(() => {
    getTrailers()
      .then((data) => Array.isArray(data) && data.length && setTrailers(data))
      .catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active === 'All' ? trailers : trailers.filter((t) => t.category === active)),
    [active, trailers]
  );

  return (
    <section id="trailers" className="relative bg-[#0d1210] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-amber-500 text-xs tracking-[0.3em] uppercase font-semibold mb-3">
              Our Fleet
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none">
              Our Rental
              <br />
              <span className="text-amber-500">Trailers</span>
            </h2>
            <p className="mt-5 text-white/70 max-w-xl text-base leading-relaxed">
              Four purpose-built trailers, each ready to work — winches, straps, chains,
              and ramps included. Pick the one that fits your job.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                  active === c
                    ? 'bg-amber-500 text-[#0d1210] border-amber-500'
                    : 'bg-white/5 text-white/80 border-white/15 hover:border-amber-500/50 hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((t) => (
            <TrailerCard key={t.id} trailer={t} onBook={onBook} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TrailerCard({ trailer, onBook }) {
  const [expanded, setExpanded] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const gallery = trailer.gallery && trailer.gallery.length > 0 ? trailer.gallery : [trailer.image];
  const featuresToShow = expanded ? trailer.features : trailer.features?.slice(0, 3);

  const prevImg = (e) => {
    e.stopPropagation();
    setImgIdx((i) => (i === 0 ? gallery.length - 1 : i - 1));
  };
  const nextImg = (e) => {
    e.stopPropagation();
    setImgIdx((i) => (i === gallery.length - 1 ? 0 : i + 1));
  };

  return (
    <div className="card-hover group relative bg-[#141a17] border border-white/10 rounded-xl overflow-hidden flex flex-col">
      <div className="relative aspect-[16/10] overflow-hidden bg-[#0d1210]">
        <img
          src={gallery[imgIdx]}
          alt={`${trailer.name} ${trailer.category.toLowerCase()} trailer for rent in Olympia, WA`}
          width={1600}
          height={1000}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1210] via-transparent to-transparent" />
        {trailer.tag && (
          <Badge className="absolute top-4 left-4 bg-amber-500 hover:bg-amber-500 text-[#0d1210] font-semibold border-0 uppercase tracking-wider">
            {trailer.tag}
          </Badge>
        )}
        <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/90 backdrop-blur-sm border border-amber-400/60">
          <Weight className="w-3.5 h-3.5 text-[#0d1210]" />
          <span className="text-xs font-semibold text-[#0d1210]">{trailer.payload}</span>
        </div>
        <div className="absolute bottom-4 left-4">
          <div className="text-[10px] font-semibold text-amber-500 tracking-[0.2em] uppercase">
            {trailer.category}
          </div>
        </div>

        {/* Image carousel controls */}
        {gallery.length > 1 && (
          <>
            <button
              onClick={prevImg}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-amber-500 hover:text-[#0d1210] text-white backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextImg}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-amber-500 hover:text-[#0d1210] text-white backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
              aria-label="Next photo"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-4 right-4 flex gap-1.5">
              {gallery.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImgIdx(i);
                  }}
                  className={`h-1.5 rounded-full transition-all ${
                    i === imgIdx ? 'w-6 bg-amber-500' : 'w-1.5 bg-white/50 hover:bg-white'
                  }`}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        {trailer.manufacturer && (
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">
            {trailer.manufacturer}
            {trailer.year ? ` · ${trailer.year}` : ''}
          </div>
        )}
        <h3 className="font-display text-2xl text-white leading-tight mt-1">{trailer.name}</h3>

        {/* spec chips — order: max payload → dimensions → axles → deck */}
        {(trailer.payload || trailer.dimensions || trailer.axles || trailer.deck) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {trailer.payload && (
              <SpecChip icon={Weight} label={`Max Payload ${trailer.payload}`} highlight />
            )}
            {trailer.dimensions && (
              <SpecChip icon={Ruler} label={trailer.dimensions} />
            )}
            {trailer.axles && (
              <SpecChip icon={TruckIcon} label={trailer.axles} />
            )}
            {trailer.deck && (
              <SpecChip icon={Layers} label={trailer.deck} />
            )}
          </div>
        )}

        {/* best for */}
        {trailer.bestFor && trailer.bestFor.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-semibold text-amber-500 tracking-[0.2em] uppercase mb-2">
              Best For
            </div>
            <div className="flex flex-wrap gap-1.5">
              {trailer.bestFor.map((b) => (
                <span
                  key={b}
                  className="text-xs text-white/80 bg-white/5 border border-white/10 rounded-full px-2.5 py-1"
                >
                  {b}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* features */}
        <ul className="mt-4 space-y-2">
          {featuresToShow?.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-white/70">
              <Check className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {trailer.features && trailer.features.length > 3 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-2 flex items-center gap-1 text-xs text-amber-500 hover:text-amber-400 font-medium"
          >
            {expanded ? (
              <>
                Show less <ChevronUp className="w-3.5 h-3.5" />
              </>
            ) : (
              <>
                +{trailer.features.length - 3} more features <ChevronDown className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        )}

        <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-4 gap-2">
          <PriceCell label="Hourly" value={`$${trailer.pricing.hourly}`} sub="/ hr" />
          <PriceCell label="Mon–Thu" value={`$${trailer.pricing.weekday}`} sub="24 hr" />
          <PriceCell label="Fri–Sun" value={`$${trailer.pricing.weekend}`} sub="24 hr" />
          <PriceCell label="Weekly" value={`$${trailer.pricing.weekly}`} sub="7 day" highlight />
        </div>

        <Button
          onClick={() => onBook && onBook(trailer)}
          className="mt-6 bg-white text-[#0d1210] hover:bg-amber-500 hover:text-[#0d1210] font-semibold rounded-md h-11 group/btn"
        >
          Book This Trailer
          <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
        </Button>
        <Link
          to={`/trailers/${trailer.id}`}
          className="mt-3 flex items-center justify-center gap-1.5 text-sm text-amber-500 hover:text-amber-400 font-medium"
        >
          View full specs & availability
          <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function SpecChip({ icon: Icon, label, highlight = false }) {
  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
        highlight
          ? 'bg-amber-500/15 border-amber-500/40 text-white'
          : 'bg-white/5 border-white/10 text-white/80'
      }`}
    >
      <Icon className={`w-3 h-3 ${highlight ? 'text-amber-400' : 'text-amber-500'}`} />
      <span className={`text-[11px] ${highlight ? 'font-semibold' : ''}`}>{label}</span>
    </div>
  );
}

function PriceCell({ label, value, sub, highlight = false }) {
  return (
    <div
      className={`rounded-md px-2 py-2 ${
        highlight ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-white/5 border border-white/10'
      }`}
    >
      <div className="text-[9px] font-semibold tracking-[0.12em] text-white/50 uppercase">
        {label}
      </div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className={`font-display text-xl ${highlight ? 'text-amber-500' : 'text-white'}`}>
          {value}
        </span>
        <span className="text-[9px] text-white/50">{sub}</span>
      </div>
    </div>
  );
}
